import React, { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import './DocumentsModal.css';

// ── File type icons ───────────────────────────────────────
const TYPE_ICON = {
  pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
  png: '🖼️', jpg: '🖼️', jpeg: '🖼️', ppt: '📊', pptx: '📊',
  txt: '📃', zip: '🗜️', rar: '🗜️', mp4: '🎬', mp3: '🎵',
};
function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  return TYPE_ICON[ext] || '📁';
}
function fileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' Ko';
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

// ── Single document card ──────────────────────────────────
function DocCard({ doc, bureau, canDeclasser, canDelete, onDeclasser, onDelete, onDownload }) {
  return (
    <div className="doc-item">
      <div className="di-icon">{fileIcon(doc.name)}</div>
      <div className="di-info">
        <div className="di-name" title={doc.name}>{doc.name}</div>
        <div className="di-meta">
          {doc.client && <span className="di-tag di-tag-client">👤 {doc.client}</span>}
          {doc.categorie && <span className="di-tag di-tag-cat">{doc.categorie}</span>}
          <span className="di-size">{doc.size}</span>
          <span className="di-date">{doc.dateAjout}</span>
        </div>
        {doc.note && <div className="di-note">💬 {doc.note}</div>}
      </div>
      <div className="di-actions">
        {doc.data && (
          <button className="dia dia-dl" title="Télécharger" onClick={() => onDownload(doc)}>⬇</button>
        )}
        {canDeclasser && bureau === 'prive' && (
          <button className="dia dia-move" title="Déclasser vers Bureau Commun" onClick={() => onDeclasser(doc.id)}>
            📤
          </button>
        )}
        {canDeclasser && bureau === 'commun' && (
          <button className="dia dia-move-back" title="Reclasser en Bureau Privé" onClick={() => onDeclasser(doc.id, true)}>
            🔒
          </button>
        )}
        {canDelete && (
          <button className="dia dia-del" title="Supprimer" onClick={() => onDelete(doc.id, bureau)}>✕</button>
        )}
      </div>
    </div>
  );
}

// ── Upload zone ───────────────────────────────────────────
function UploadZone({ bureau, onUpload }) {
  const [client,    setClient]    = useState('');
  const [categorie, setCategorie] = useState('Contrat');
  const [note,      setNote]      = useState('');
  const [pending,   setPending]   = useState([]);
  const inputRef = useRef();

  const CATS_PRIVE  = ['Contrat','Dossier client','Facture signée','Acte notarié','Pièce d\'identité','Certificat','Autre'];
  const CATS_COMMUN = ['Modèle de document','Référence technique','Procédure interne','Formulaire','Catalogue','Autre'];
  const cats = bureau === 'prive' ? CATS_PRIVE : CATS_COMMUN;

  function handleFiles(e) {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        setPending(p => [...p, {
          id: Date.now() + Math.random(),
          name: file.name,
          size: fileSize(file.size),
          data: ev.target.result,
          type: file.type,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }

  function removePending(id) { setPending(p => p.filter(f => f.id !== id)); }

  function handleSave() {
    if (pending.length === 0) return;
    const now = new Date().toLocaleDateString('fr-FR');
    pending.forEach(f => {
      onUpload(bureau, {
        id: Date.now() + Math.random(),
        name: f.name, size: f.size, data: f.data,
        client:    bureau === 'prive' ? (client.trim() || '—') : '',
        categorie: categorie,
        note:      note.trim(),
        dateAjout: now,
      });
    });
    setPending([]); setClient(''); setNote('');
  }

  return (
    <div className="upload-zone">
      <div className="uz-head">
        📤 Ajouter des documents — {bureau === 'prive' ? '🔐 Bureau Privé' : '🤝 Bureau Commun'}
      </div>
      <div className="uz-fields">
        {bureau === 'prive' && (
          <div className="uz-field">
            <label>👤 Client concerné</label>
            <input value={client} onChange={e => setClient(e.target.value)} placeholder="Nom du client ou dossier" />
          </div>
        )}
        <div className="uz-field">
          <label>📂 Catégorie</label>
          <select value={categorie} onChange={e => setCategorie(e.target.value)}>
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="uz-field">
          <label>💬 Note (optionnel)</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Description, référence…" />
        </div>
      </div>

      <div
        className="uz-dropzone"
        onClick={() => inputRef.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFiles({ target: { files: e.dataTransfer.files }, value:'' }); }}
      >
        <div className="uz-drop-icon">📁</div>
        <p>Cliquez ou déposez vos fichiers ici</p>
        <p className="uz-hint">PDF, Word, Excel, Images… · Taille max 10 Mo/fichier</p>
        <input ref={inputRef} type="file" multiple style={{ display:'none' }} onChange={handleFiles} />
      </div>

      {pending.length > 0 && (
        <div className="uz-pending">
          <div className="uzp-head">{pending.length} fichier(s) en attente</div>
          {pending.map(f => (
            <div key={f.id} className="uzp-item">
              <span>{fileIcon(f.name)}</span>
              <span className="uzp-name">{f.name}</span>
              <span className="uzp-size">{f.size}</span>
              <button onClick={() => removePending(f.id)}>✕</button>
            </div>
          ))}
          <button className="abtn abtn-green" style={{ marginTop:10 }} onClick={handleSave}>
            ✓ Enregistrer {pending.length} fichier(s)
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────
export default function DocumentsModal({ onClose }) {
  const { currentUser, data, updateData, showToast } = useApp();
  const isAdmin   = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';

  const [tab,    setTab]    = useState(isAdmin ? 'prive' : 'commun');
  const [search, setSearch] = useState('');

  // Access guard (after all hooks)
  if (!isAdmin && !isManager) return null;

  // ── Helpers ──────────────────────────────────────────
  function addDoc(bureau, doc) {
    if (bureau === 'prive') {
      updateData({ docsPrives: [...(data.docsPrives || []), doc] });
    } else {
      updateData({ docsCommun: [...(data.docsCommun || []), doc] });
    }
    showToast('Document ajouté avec succès !');
  }

  function deleteDoc(id, bureau) {
    if (bureau === 'prive') {
      updateData({ docsPrives: (data.docsPrives || []).filter(d => d.id !== id) });
    } else {
      updateData({ docsCommun: (data.docsCommun || []).filter(d => d.id !== id) });
    }
    showToast('Document supprimé.', 'warn');
  }

  // Admin: move from private to shared OR back
  function declasser(id, toPrivate = false) {
    if (toPrivate) {
      // Bureau commun → Privé (reclassification)
      const doc = (data.docsCommun || []).find(d => d.id === id);
      if (!doc) return;
      updateData({
        docsCommun:  (data.docsCommun || []).filter(d => d.id !== id),
        docsPrives:  [...(data.docsPrives || []), { ...doc, reclasse: new Date().toLocaleDateString('fr-FR') }],
      });
      showToast('Document reclassé en Bureau Privé.');
    } else {
      // Bureau privé → Commun (déclassement)
      const doc = (data.docsPrives || []).find(d => d.id === id);
      if (!doc) return;
      updateData({
        docsPrives: (data.docsPrives || []).filter(d => d.id !== id),
        docsCommun: [...(data.docsCommun || []), { ...doc, declasse: new Date().toLocaleDateString('fr-FR') }],
      });
      showToast('Document déclassé vers Bureau Commun.');
    }
  }

  function downloadDoc(doc) {
    if (!doc.data) return;
    const a = document.createElement('a');
    a.href = doc.data; a.download = doc.name; a.click();
  }

  // Filter docs by search
  function filtered(docs) {
    if (!search.trim()) return docs || [];
    const q = search.toLowerCase();
    return (docs || []).filter(d =>
      d.name?.toLowerCase().includes(q) ||
      d.client?.toLowerCase().includes(q) ||
      d.categorie?.toLowerCase().includes(q) ||
      d.note?.toLowerCase().includes(q)
    );
  }

  const priveDocs  = filtered(data.docsPrives);
  const communDocs = filtered(data.docsCommun);

  const tabs = [
    ...(isAdmin ? [{ id:'prive',  label:'🔐 Bureau Privé', count: (data.docsPrives||[]).length, desc:'Admin uniquement — dossiers clients confidentiels' }] : []),
    {              id:'commun', label:'🤝 Bureau Commun', count: (data.docsCommun||[]).length, desc:'Admin & Manager — documents de service partagés' },
    ...(isAdmin ? [{ id:'upload', label:'📤 Ajouter',  count: null }] : [{ id:'upload', label:'📤 Ajouter', count: null }]),
  ];

  return (
    <div className="overlay open">
      <div className="modal modal-xl">
        {/* Header */}
        <div className="mhead">
          <div>
            <h2>📂 Gestion Documentaire Interne</h2>
            <p className="docs-modal-sub">
              {isAdmin
                ? '🔐 Accès Administrateur — Bureau Privé & Bureau Commun'
                : '🤝 Accès Manager — Bureau Commun uniquement'}
            </p>
          </div>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>

        {/* Search bar */}
        <div className="docs-search-bar">
          <input
            className="docs-search"
            placeholder="🔍 Rechercher par nom, client, catégorie…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="docs-search-clear" onClick={() => setSearch('')}>✕</button>}
        </div>

        {/* Tabs */}
        <div className="docs-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`docs-tab ${tab === t.id ? 'active' : ''} ${t.id === 'prive' ? 'tab-prive' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.count !== null && <span className="docs-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>

        {/* Tab description */}
        {tab !== 'upload' && (
          <div className={`docs-bureau-desc ${tab === 'prive' ? 'desc-prive' : 'desc-commun'}`}>
            {tab === 'prive'
              ? '🔐 Ces documents sont strictement confidentiels. Seul l\'administrateur y a accès. Vous pouvez déclasser un document vers le Bureau Commun (📤) pour le partager avec le manager.'
              : isAdmin
                ? '🤝 Ces documents sont accessibles et utilisables par l\'administrateur et le manager. Vous pouvez reclasser un document en Bureau Privé (🔒) si nécessaire.'
                : '🤝 Ces documents sont mis à votre disposition par l\'administrateur pour vos besoins de service.'}
          </div>
        )}

        {/* Content */}
        <div className="docs-content">

          {/* ── Bureau Privé (admin only) ── */}
          {tab === 'prive' && isAdmin && (
            <div>
              {priveDocs.length === 0 ? (
                <div className="docs-empty">
                  <div style={{fontSize:48}}>🔐</div>
                  <p>Bureau Privé vide</p>
                  <p className="docs-empty-hint">
                    Ajoutez des dossiers clients confidentiels via l'onglet 📤 Ajouter.
                  </p>
                </div>
              ) : (
                <>
                  <div className="docs-count-bar">
                    {priveDocs.length} document(s) — cliquez sur 📤 pour déclasser vers le Bureau Commun
                  </div>
                  <div className="docs-list">
                    {priveDocs.map(doc => (
                      <DocCard
                        key={doc.id} doc={doc} bureau="prive"
                        canDeclasser={true} canDelete={true}
                        onDeclasser={declasser}
                        onDelete={deleteDoc}
                        onDownload={downloadDoc}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Bureau Commun ── */}
          {tab === 'commun' && (
            <div>
              {communDocs.length === 0 ? (
                <div className="docs-empty">
                  <div style={{fontSize:48}}>🤝</div>
                  <p>Bureau Commun vide</p>
                  <p className="docs-empty-hint">
                    {isAdmin
                      ? 'Ajoutez des documents de service partagés ou déclassez des documents du Bureau Privé.'
                      : 'L\'administrateur n\'a pas encore partagé de documents.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="docs-count-bar">
                    {communDocs.length} document(s) accessible(s)
                    {isAdmin && ' — cliquez sur 🔒 pour reclasser en Bureau Privé'}
                  </div>
                  <div className="docs-list">
                    {communDocs.map(doc => (
                      <DocCard
                        key={doc.id} doc={doc} bureau="commun"
                        canDeclasser={isAdmin}
                        canDelete={isAdmin}
                        onDeclasser={declasser}
                        onDelete={deleteDoc}
                        onDownload={downloadDoc}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Upload ── */}
          {tab === 'upload' && (
            <div className="docs-upload-panel">
              {isAdmin && (
                <UploadZone bureau="prive" onUpload={addDoc} />
              )}
              <UploadZone bureau="commun" onUpload={addDoc} />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
